import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Fingerprint,
  KeyRound,
  Loader2,
  Lock,
  LogIn,
  ShieldCheck,
  Smartphone,
  Sparkles,
  User as UserIcon,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Button,
  LanguageToggle,
  ThemeToggle,
  cn,
  useToast,
} from '@solara/ui-shared';
import { useSession } from '../auth/SessionContext';

export default function LoginPage() {
  const { t } = useTranslation('login');
  const { t: tP1 } = useTranslation('p1');
  const { locale } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MFA challenge state — kicks in when the backend returns mfa-required.
  const [mfaChallenge, setMfaChallenge] = useState<{ token: string } | null>(null);
  const [mfaInput, setMfaInput] = useState('');

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'long' }),
    [locale],
  );

  useEffect(() => {
    if (session.isAuthenticated) navigate(from, { replace: true });
  }, [session.isAuthenticated, navigate, from]);

  if (session.isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await session.signIn(username, password);
    setSubmitting(false);
    if (!res.ok) {
      if (res.reason === 'required') setError(t('errors.required'));
      else if (res.reason === 'mfa-required') {
        setMfaChallenge({ token: res.mfaChallengeToken });
        return;
      } else if (res.reason === 'wrong-portal') {
        setError(
          'Ez egy customer fiók. Jelentkezz be az Ügyfél-portálon: http://localhost:5174',
        );
      } else setError(t('errors.invalid'));
      return;
    }
    navigate(from, { replace: true });
  }

  async function handleMfaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mfaChallenge) return;
    setSubmitting(true);
    setError(null);
    const res = await session.completeMfa(mfaChallenge.token, mfaInput);
    setSubmitting(false);
    if (!res.ok) {
      setError('Invalid MFA token — try again.');
      return;
    }
    navigate(from, { replace: true });
  }

  function ssoComingSoon(provider: string) {
    toast({ title: provider, description: t('sso.comingSoon'), tone: 'info' });
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Top bar (compact) */}
      <header className="h-14 px-4 sm:px-6 lg:px-10 flex items-center gap-3 border-b border-border bg-surface/80 backdrop-blur">
        <div className="h-8 w-8 rounded-md bg-brand-600 grid place-items-center text-white font-semibold">A</div>
        <div className="leading-tight">
          <div className="text-sm font-semibold">Solara Platform</div>
          <div className="text-[11px] text-fg-subtle">{t('page.adminSubtitle')}</div>
        </div>
        <div className="ms-auto flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
      </header>

      <main className="flex-1 grid lg:grid-cols-2">
        {/* Hero panel */}
        <section
          className={cn(
            'relative overflow-hidden hidden lg:flex flex-col justify-between',
            'p-8 xl:p-12 text-white',
            'bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800',
          )}
        >
          {/* Decorative watermarks — kept behind content and at very low opacity. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
            <Sparkles className="absolute -end-24 -top-24 h-64 w-64 text-white opacity-[0.06]" />
            <Sparkles className="absolute -start-20 -bottom-24 h-80 w-80 text-white opacity-[0.04]" />
          </div>

          <div className="relative z-10 space-y-8 max-w-xl">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-white/70">
                {t('hero.adminEyebrow')}
              </p>
              <h1 className="text-3xl xl:text-4xl font-semibold leading-tight">
                {t('hero.adminHeadline')}
              </h1>
              <p className="text-white/85 leading-relaxed">{t('hero.adminLead')}</p>
            </div>

            {/* Compliance chips */}
            <ul className="flex flex-wrap gap-2">
              {[
                t('compliance.fta'),
                t('compliance.iso27001'),
                t('compliance.iso22301'),
                t('compliance.mtls'),
              ].map((label) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs font-medium border border-white/15"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {label}
                </li>
              ))}
            </ul>

            {/* 2FA marketing tile */}
            <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm p-5 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Lock className="h-4 w-4" />
                {t('twofa.ribbonTitle')}
              </div>
              <p className="text-sm text-white/85">{t('twofa.ribbonBody')}</p>
              <ul className="space-y-2 text-sm">
                <TwoFaRow icon={<Smartphone className="h-4 w-4" />} label={t('twofa.totp')} />
                <TwoFaRow icon={<KeyRound className="h-4 w-4" />} label={t('twofa.sms')} />
                <TwoFaRow icon={<Fingerprint className="h-4 w-4" />} label={t('twofa.hardware')} />
              </ul>
              <p className="text-[11px] text-white/60 mt-2">{t('twofa.demoNote')}</p>
            </div>
          </div>

          <div className="relative z-10 pt-8 flex items-center gap-4">
            <p className="text-[11px] text-white/60">{dateFmt.format(new Date())}</p>
          </div>
        </section>

        {/* Form panel */}
        <section className="flex flex-col items-center justify-center p-6 sm:p-10 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t('page.adminTitle')}</h2>
              <p className="text-sm text-fg-muted lg:hidden">{t('hero.adminLead')}</p>
            </div>

            {/* Demo credentials helper — only meaningful in the dev seed. */}
            <div className="rounded-xl border border-brand-500/30 bg-brand-50/60 px-4 py-3 text-sm space-y-2">
              <p className="font-medium text-brand-900">{tP1('loginDemo.adminTitle')}</p>
              <p className="text-xs text-fg-muted">
                {tP1('loginDemo.clickEmailFill')}{' '}
                <code className="rounded bg-white/60 px-1.5 py-0.5 text-[11px]">Budapest1928!</code>
              </p>
              <ul className="space-y-1">
                {[
                  { email: 'admin@solara.ae', role: 'PlatformAdmin' },
                  { email: 'compliance@solara.ae', role: 'ComplianceOfficer' },
                  { email: 'ops@solara.ae', role: 'OperationsViewer' },
                  { email: 'security@solara.ae', role: 'SecurityOfficer' },
                ].map((u) => (
                  <li key={u.email} className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setUsername(u.email);
                        setPassword('Budapest1928!');
                      }}
                      className="text-xs text-brand-700 hover:underline font-mono"
                    >
                      {u.email}
                    </button>
                    <span className="text-[10px] text-fg-subtle">{u.role}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-fg-subtle">
                {tP1('loginDemo.customerPortalLink')}{' '}
                <a href="http://localhost:5174" className="text-brand-700 hover:underline">
                  localhost:5174
                </a>
              </p>
            </div>

            {/* Mobile-only trust strip — desktop already shows the hero. */}
            <ul className="lg:hidden flex flex-wrap gap-1.5 -mt-1">
              <TrustChip icon={<ShieldCheck className="h-3 w-3" />} label={t('compliance.fta')} />
              <TrustChip icon={<Lock className="h-3 w-3" />} label={t('twofa.ribbonTitle')} />
              <TrustChip icon={<KeyRound className="h-3 w-3" />} label={t('compliance.mtls')} />
            </ul>

            {mfaChallenge ? (
              <form onSubmit={handleMfaSubmit} className="space-y-4" noValidate>
                <div className="rounded-lg border border-brand-500/30 bg-brand-50 px-3 py-2 text-sm">
                  <p className="font-medium">Two-factor verification</p>
                  <p className="text-xs text-fg-muted">
                    Open your authenticator app and enter the 6-digit code for {username}.
                  </p>
                </div>
                <input
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={mfaInput}
                  onChange={(e) => setMfaInput(e.target.value.replace(/\s/g, ''))}
                  placeholder="123456"
                  className="w-full h-11 px-3 rounded-lg bg-surface border border-border text-sm tracking-[0.4em] text-center"
                />
                {error && (
                  <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" size="lg" className="flex-1 gap-2" disabled={submitting || mfaInput.length < 6}>
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-4 w-4" />
                        Verify
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    variant="ghost"
                    onClick={() => {
                      setMfaChallenge(null);
                      setMfaInput('');
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Username */}
              <div className="space-y-1.5">
                <label htmlFor="username" className="text-sm font-medium">
                  {t('form.username')}
                </label>
                <div className="relative">
                  <UserIcon className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
                  <input
                    id="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('form.usernamePlaceholder')}
                    dir="ltr"
                    className="w-full h-11 ps-10 pe-3 rounded-lg bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:border-brand-500"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label htmlFor="password" className="text-sm font-medium flex items-center justify-between">
                  <span>{t('form.password')}</span>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toast({ title: t('form.forgotPassword'), description: t('sso.comingSoon'), tone: 'info' });
                    }}
                    className="text-xs font-normal text-brand-700 hover:underline"
                  >
                    {t('form.forgotPassword')}
                  </a>
                </label>
                <div className="relative">
                  <Lock className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('form.passwordPlaceholder')}
                    dir="ltr"
                    className="w-full h-11 ps-10 pe-11 rounded-lg bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:border-brand-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? t('form.hidePassword') : t('form.showPassword')}
                    className="absolute inset-y-0 end-2 my-auto h-7 w-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-muted"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Remember */}
              <label className="flex items-center gap-2 text-sm select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-border text-brand-600 focus-visible:ring-2 focus-visible:ring-brand-500/40"
                />
                <span className="text-fg-muted">{t('form.rememberMe')}</span>
              </label>

              {/* Error */}
              {error ? (
                <div className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button
                type="submit"
                size="lg"
                className="w-full gap-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('form.submitting')}
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 rtl-mirror" />
                    {t('form.submit')}
                  </>
                )}
              </Button>
            </form>
            )}

            {/* SSO */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wider text-fg-subtle">{t('sso.title')}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <SsoButton onClick={() => ssoComingSoon('Microsoft 365')} label="Microsoft" mark="MS" />
                <SsoButton onClick={() => ssoComingSoon('Google Workspace')} label="Google" mark="G" />
                <SsoButton onClick={() => ssoComingSoon('OpenID Connect')} label="OpenID" mark="ID" />
              </div>
              <p className="text-[11px] text-fg-subtle text-center">{t('sso.comingSoon')}</p>
            </div>
          </div>

          <footer className="mt-12 text-[11px] text-fg-subtle flex items-center gap-4">
            <span>{t('footer.rights')}</span>
            <span aria-hidden>·</span>
            <a href="#" className="hover:text-fg">{t('footer.privacy')}</a>
            <a href="#" className="hover:text-fg">{t('footer.terms')}</a>
            <a href="#" className="hover:text-fg">{t('footer.status')}</a>
          </footer>
        </section>
      </main>
    </div>
  );
}

function TwoFaRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="flex items-center gap-2 text-white/90">
      <span className="h-7 w-7 rounded-md bg-white/15 grid place-items-center shrink-0">
        {icon}
      </span>
      <span>{label}</span>
    </li>
  );
}

function TrustChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-fg-muted">
      <span className="text-brand-600">{icon}</span>
      <span>{label}</span>
    </li>
  );
}

function SsoButton({
  onClick,
  label,
  mark,
}: {
  onClick: () => void;
  label: string;
  mark: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-10 rounded-lg border border-border bg-surface hover:bg-surface-muted text-sm font-medium text-fg-muted hover:text-fg flex items-center justify-center gap-2 transition-colors"
    >
      <span className="h-6 w-6 rounded-md bg-brand-50 text-brand-700 grid place-items-center text-[10px] font-bold">
        {mark}
      </span>
      {label}
    </button>
  );
}
